import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, Select, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { voltageDrop, type Phase, type Conductor, type ResistivityMethod } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/voltage-drop")({
  component: VoltageDropPage,
});

function VoltageDropPage() {
  const [phase, setPhase] = useState<"1" | "3">("1");
  const [length, setLength] = useState<number | "">("");
  const [current, setCurrent] = useState<number | "">("");
  const [cs, setCs] = useState<number | "">(2.5);
  const [voltage, setVoltage] = useState<number | "">(230);
  const [material, setMaterial] = useState<Conductor>("cu");
  const [cosPhi, setCos] = useState<number | "">(1);
  const [method, setMethod] = useState<ResistivityMethod>("strict");

  const result = useMemo(() => {
    if ([length, current, cs, voltage, cosPhi].some((v) => v === "")) return null;
    try {
      return voltageDrop({
        phase: Number(phase) as Phase,
        length: length as number, current: current as number,
        crossSection: cs as number, voltage: voltage as number,
        material, cosPhi: cosPhi as number, method,
      });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [phase, length, current, cs, voltage, material, cosPhi, method]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Spenningsfall" subtitle="Beregner ΔU i volt og prosent." />
      <CalcShell
        title="Inndata"
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title="Spenningsfall"
              rows={[
                { label: "ΔU", value: `${fmt(result.dropVolt, 2)} V`, strong: true },
                { label: "ΔU %", value: `${fmt(result.dropPercent, 2)} %`, strong: true },
                { label: "≤ 3 % (lys)",   value: result.withinLighting ? "✓ innenfor" : "✗ overskredet" },
                { label: "≤ 5 % (øvrig)", value: result.withinGeneral ? "✓ innenfor" : "✗ overskredet" },
                { label: "ρ brukt", value: `${fmt(result.resistivity, 4)} Ω·mm²/m` },
              ]}
              note={`${result.methodNote}. Forenklet resistiv modell. Anbefalte grenser er veiledende (NEK 400).`}
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Spenningsfall`} lines={linesFromElectricianNote(`Kabel: dimensjonert for spenningsfall`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll ut alle felt.</div>
        }
      >
        <Field label="Fase">
          <Select value={phase} onChange={setPhase} options={[{ value: "1", label: "1-fase" }, { value: "3", label: "3-fase" }]} />
        </Field>
        <Field label="Lengde" unit="m"><NumberInput value={length} onChange={setLength} /></Field>
        <Field label="Strøm" unit="A"><NumberInput value={current} onChange={setCurrent} /></Field>
        <Field label="Tverrsnitt" unit="mm²"><NumberInput value={cs} onChange={setCs} /></Field>
        <Field label="Spenning" unit="V"><NumberInput value={voltage} onChange={setVoltage} /></Field>
        <Field label="Materiale">
          <Select value={material} onChange={setMaterial} options={[{ value: "cu", label: "Kobber" }, { value: "al", label: "Aluminium" }]} />
        </Field>
        <Field label="Metode">
          <Select
            value={method}
            onChange={setMethod}
            options={[
              { value: "strict", label: "Strict – konservativ (Store Blå)" },
              { value: "iec", label: "IEC 60364-5-52 Annex G" },
              { value: "nelfo", label: "Nelfo lærebok (ρ₂₀ × 1,2)" },
              { value: "physical", label: "Fysisk (ρ₂₀ + α, 70 °C)" },
            ]}
          />
        </Field>
        <Field label="cosφ"><NumberInput value={cosPhi} onChange={setCos} /></Field>
      </CalcShell>
    </>
  );
}
